import { DatePicker, Modal } from "antd";
import { motion } from "framer-motion";
import Textarea from "../../../../Components/Textarea";
import Input from "../../../../Components/Input";
import dayjs from "dayjs";
import { useCreateCustomerDiscountMutation } from "../../../../hooks/DiscountManagement/useCreateDiscountMutation";
import Button from "../../../../Components/Button";
import { formatNumberWithCommas } from "../../../../utils/formatNumber";
import { ChangeEvent, FormEvent, useState } from "react";
import Loader from "Components/Loader";
import { useDebounce } from "hooks/useDebounce";
import { useGetMeQuery } from "hooks/Auth/useGetMeQuery";
import { useGetCustomerQuery } from "hooks/CustomerManagement/useGetCustomerQuery";

const CustomerDiscountForm = () => {
  const { data } = useGetMeQuery();
  const [payload, setPayload] = useState<any>({
    discountType: "customer",
    description: "",
    startDate: "",
    endDate: "",
    flatAmountOff: "",
    applicableCustomers: [],
    usageLimit: "",
    createdBy: data?.data?.id,
  });

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<any>({});
  const debouncedSearch = useDebounce(search);

  const { data: customers, isPending: isLoading } = useGetCustomerQuery({
    search: debouncedSearch,
  });

  const { mutateAsync, isPending } = useCreateCustomerDiscountMutation(payload);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutateAsync();
  };

  const { RangePicker } = DatePicker;

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPayload((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleUserSelection = (user: any) => {
    const id = user.id;
    const isSelected = !!selectedUsers[id];

    const updatedSelectedUsers = { ...selectedUsers };
    if (isSelected) {
      delete updatedSelectedUsers[id];
    } else {
      updatedSelectedUsers[id] = true;
    }

    const updatedCustomers = isSelected
      ? payload.applicableCustomers.filter(
          (customerId: string) => customerId !== id
        )
      : [...payload.applicableCustomers, id];

    setSelectedUsers(updatedSelectedUsers);
    setPayload((prevPayload: any) => ({
      ...prevPayload,
      applicableCustomers: updatedCustomers,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div>
        <div className="flex gap-[10px] mb-2 text-grey text-[16px] manrope capitalize">
          <div className="w-6/12">Start Date</div>
          <div className="w-6/12">End Date</div>
        </div>
        <RangePicker
          showTime
          className="p-3 mb-3 text-[16px] bg-white w-full border-[1px] duration-300 border-[#d3d3d3] focus:border-orange rounded-md"
          value={
            startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null
          }
          onCalendarChange={(dates, dateStrings) => {
            if (dates && dates[0] && dates[1]) {
              const startDate = dates[0].format("YYYY-MM-DD");
              const endDate = dates[1].format("YYYY-MM-DD");

              setStartDate(startDate);
              setEndDate(endDate);

              setPayload({ ...payload, startDate, endDate });
            }
          }}
        />
        <Input
          name="flatAmountOff"
          type="text"
          placeholder="Flat Amount Off"
          value={formatNumberWithCommas(payload?.flatAmountOff)}
          onChange={handleInputChange}
          label={"Flat Amount Off"}
        />
        <Input
          name="usageLimit"
          type="text"
          placeholder="Usage Limit"
          value={formatNumberWithCommas(payload?.usageLimit)}
          onChange={handleInputChange}
          label={"Usage Limit"}
        />
        <button
          onClick={() => setShowCustomerModal(true)}
          className="my-2 p-3 w-full bg-primary-light rounded-md"
        >
          Select Customer(s)
        </button>
        <Modal
          title="Customers"
          open={showCustomerModal}
          height={"500px"}
          className="max-w-[40rem] !w-full"
          onCancel={() => setShowCustomerModal(false)}
          footer={
            <Button
              className="text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300"
              label={"Continue"}
              onClick={() => setShowCustomerModal(false)}
            />
          }
        >
          <div className="w-full">
            <div className="flex gap-[10px] mb-4">
              <span className="relative w-full">
                <SearchIcon />
                <input
                  type="text"
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(e.target.value)
                  }
                  className="rounded-[10px] w-full border-[1px] border-[#ababab] text-[13px] p-[13px] pl-[35px]"
                  placeholder="Search"
                />
              </span>
            </div>
            <div>
              {!customers && isLoading && <Loader />}
              {!isLoading &&
                customers?.data?.docs?.map((user: any) => (
                  <div
                    key={user.id}
                    className="mb-5 cursor-pointer"
                    onClick={() => handleUserSelection(user)}
                  >
                    <div className="flex flex-row gap-[20px]">
                      <input
                        type="checkbox"
                        checked={!!selectedUsers[user.id]}
                      />
                      <div>
                        <h3 className="font-bold">
                          {user?.firstName + " " + user?.lastName}
                        </h3>
                        <p className="text-grey">{user?.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Modal>
        <Textarea
          label="Description"
          name="description"
          value={payload?.description}
          placeholder="Description"
          onChange={handleInputChange}
        />
      </div>
      <Button
        disabled={
          !payload.description ||
          !payload.startDate ||
          !payload.endDate ||
          !payload.flatAmountOff ||
          !payload.usageLimit ||
          !payload.createdBy ||
          payload.applicableCustomers.length === 0 ||
          isPending
        }
        onClick={handleSubmit}
        isLoading={isLoading}
        label={"Complete"}
      />
    </motion.div>
  );
};

export default CustomerDiscountForm;

const SearchIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className="size-4 absolute top-[18px] left-[15px] text-[#ababab]"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  );
};
